/**
 * @fileoverview Quem vê as conversas na empresa (opção "ticketVisibility", que
 * o admin escolhe em Configurações → Conversas): "own" (só as próprias),
 * "queues" (as das filas dele, o padrão) ou "company" (a empresa toda). Admin e
 * super veem a empresa toda. Com a regra nova de acesso ligada, vale o nível
 * que a API calculou para a pessoa (o perfil, a exceção e essa opção). O
 * servidor confere a mesma regra (services/TicketServices/ticketScope.ts no
 * backend); aqui ela só monta a tela: o olho "Mostrar todos" e as filas da lista.
 */

import { useContext, useEffect } from "react";
import { AuthContext } from "../../context/Auth/AuthContext";
import { useQueues } from "../../context/Queues/QueuesContext";
import useCompanySetting from "../useCompanySetting";
import usePermission from "../usePermission";

const LEVELS = ["own", "queues", "company"];
// Última vez que alguma tela pediu as filas da empresa de novo (uma por vez).
let lastQueuesRefetch = 0;

const useTicketVisibility = () => {
  const { user } = useContext(AuthContext);
  const { enforce, ticketsView } = usePermission();
  const seesCompany = user?.profile === "admin" || !!user?.super;
  const [value] = useCompanySetting("ticketVisibility", { skip: seesCompany || enforce });
  // Sem as conversas ("none"), a lista nem abre: fica o nível mais estreito.
  if (enforce) return LEVELS.includes(ticketsView) ? ticketsView : "own";
  if (seesCompany) return "company";
  return LEVELS.includes(value) ? value : "queues";
};

/**
 * As filas da lista de conversas: as que o filtro oferece (todas, para quem vê
 * a empresa toda; senão, as do usuário) e a seleção padrão (as do usuário; o
 * atendente no nível "Da empresa toda" começa com todas). "Sem fila" entra à
 * parte, na própria lista.
 */
export const useConversationQueues = () => {
  const { user } = useContext(AuthContext);
  const { queues: companyQueues = [], loading, refetchQueues } = useQueues();
  const visibility = useTicketVisibility();
  const isAdmin = user?.profile === "admin" || !!user?.super;
  const ownQueues = user?.queues || [];
  const seesCompany = visibility === "company";

  // As filas da empresa são lidas quando o app abre; quem entrou pelo login (sem
  // recarregar a página) ainda não as tem: pede de novo, uma vez.
  useEffect(() => {
    if (!seesCompany || loading || companyQueues.length || Date.now() - lastQueuesRefetch < 10000) return;
    lastQueuesRefetch = Date.now();
    refetchQueues();
  }, [seesCompany, loading, companyQueues.length, refetchQueues]);

  // Até as filas da empresa chegarem, valem as do usuário.
  const allQueues = companyQueues.length ? companyQueues : ownQueues;
  return {
    visibility,
    filterQueues: seesCompany ? allQueues : ownQueues,
    defaultQueues: seesCompany && !isAdmin ? allQueues : ownQueues,
  };
};

export default useTicketVisibility;
